import { groupRowId, parseOwnerRow } from "@fomofi/shared";

/**
 * Old address ⇄ new address for the redesign (RD-05), one table for every move.
 *
 * With the switch on, an old address goes to its new home, so bookmarks, emails and
 * in-app links never land on a dead page. With it off, the table runs in reverse, so a
 * new address shared from dev still works on prod. Pages that keep their address (the
 * tabs under Plan, Activity, Upcoming and Insights) are not listed: nothing moves. Pages
 * the redesign retires are in `REDESIGN_RETIRED`, switch on only.
 *
 * Mobile keeps the twin of this table (report 132's mobile column); a drift test will
 * pair them once both exist.
 */
export interface RedesignMove {
  /** Today's address, what prod serves. */
  from: string;
  /** Where the page lives with the redesign on. */
  to: string;
}

export const REDESIGN_MOVES: readonly RedesignMove[] = [
  // Home replaced the Dashboard (Anthony, 2026-10-13).
  { from: "/dashboard", to: "/home" },
  // Rules moves into Settings & profile (RD-07). Paid: lib/subscriptionGate keeps it
  // behind the paywall although /settings is open.
  { from: "/rules", to: "/settings/rules" },
];

/**
 * Pages that exist only with the switch on, and where each goes with it off. Not a move:
 * a move runs in reverse, and the pages /plan falls back to are retired with the switch on
 * (`REDESIGN_RETIRED`). Mobile keeps the twin (`REDESIGN_ONLY`); the field is `fallback`,
 * not `to`, so the move twin check still compares moves only.
 */
export const REDESIGN_ONLY: readonly { path: string; fallback: string }[] = [
  // Anthony's Plan spec: "Your plan" (agreed with mobile, 2026-10-19).
  { path: "/plan", fallback: "/forecast" },
];

/**
 * Pages retired with the switch on (Paul, 2026-10-29): "More planning tools" is removed,
 * and Plan is the only place bucket amounts are edited (Anthony's "one home per number").
 * Each route lands on Plan, Goals on its Savings & goals group (ownerLink's
 * `row=group:<key>`). Switch-on only: with it off there is no Plan page, so these pages
 * stay as prod runs them. Nothing is deleted; the models and APIs stay. Mobile keeps the
 * twin (`REDESIGN_RETIRED`); the fields are `lands` and `row`, not `to`, so the move twin
 * check still compares moves only.
 */
export const REDESIGN_RETIRED: readonly { path: string; lands: string; row?: string }[] = [
  // Allocate.
  { path: "/forecast", lands: "/plan" },
  { path: "/budgets", lands: "/plan" },
  { path: "/goals", lands: "/plan", row: groupRowId("savings_goals") },
];

/**
 * Where a retired page lands with the switch on, query kept; null when it stays put. An
 * old link's `row` comes through when Plan has that row (a bucket or a group); a goal
 * row, which Plan has not, gives way to the page's own row.
 */
export function retiredRedirectFor(pathname: string, search: string, on: boolean): string | null {
  if (!on) return null;
  const retired = REDESIGN_RETIRED.find((r) => r.path === pathname);
  if (!retired) return null;
  const params = new URLSearchParams(search);
  const row = parseOwnerRow(params.get("row"));
  if (retired.row && row?.kind !== "bucket" && row?.kind !== "group") {
    params.set("row", retired.row);
  }
  const query = params.toString();
  return query ? `${retired.lands}?${query}` : retired.lands;
}

/**
 * An in-app link to `href` for the switch: a retired page's link goes straight to where
 * it lands, so it never hops through the redirect (its `#section` is Allocate's, so it
 * goes too). Anything else, or the switch off, is unchanged.
 */
export function retiredLink(href: string, on: boolean): string {
  const [pathname, query] = href.split("#")[0].split("?");
  return retiredRedirectFor(pathname, query ? `?${query}` : "", on) ?? href;
}

/**
 * Addresses a third party returns to. They are registered with Plaid and Stripe, so
 * they are never moved or redirected, switch on or off.
 */
export const NEVER_REDIRECTED: readonly string[] = [
  "/banks/oauth-return",
  "/payment",
  "/payment/success",
  "/payment/return",
];

/**
 * Where `pathname` should go given the switch, or null when it stays put. Exact path
 * match: a move is a whole page, and nothing under these paths has its own route.
 */
export function redesignRedirectFor(pathname: string, on: boolean): string | null {
  if (NEVER_REDIRECTED.includes(pathname)) return null;
  for (const move of REDESIGN_MOVES) {
    if (on && pathname === move.from) return move.to;
    if (!on && pathname === move.to) return move.from;
  }
  if (!on) {
    const only = REDESIGN_ONLY.find((p) => p.path === pathname);
    if (only) return only.fallback;
  }
  return null;
}
